import { z } from 'zod';

import { socialApiClient } from '@/shared/api-client/social-api-client';
import type { UtmParams } from '@/shared/track/utm';
import { omitNullish } from '@/shared/utils/omit-nullish';
import type { USER_ROLE } from '../user/constants';
import { isTokenExpired } from './jwt-utils';
import { SentryUserContext } from './utils/sentry-user-context';

const TOKEN_STORAGE_KEY = '@@sy__tks@'; // @@syncly__tokens@

const UglifiedAuthTokenSchema = z.object({
  ak: z.string(), // access token
  kr: z.string(), // refresh token
});

export type UglifiedAuthTokens = z.infer<typeof UglifiedAuthTokenSchema>;

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

export type OAuthVendor = 'google' | 'microsoft';

export const AuthService = {
  setTokens(tokens: AuthTokens): void {
    const uglifiedTokens: UglifiedAuthTokens = {
      ak: tokens.accessToken,
      kr: tokens.refreshToken,
    };

    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(uglifiedTokens));
  },

  getTokens(): AuthTokens | null {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = UglifiedAuthTokenSchema.parse(JSON.parse(stored));

    return {
      accessToken: parsed.ak,
      refreshToken: parsed.kr,
    };
  },

  removeTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    // Clear Sentry user context on logout
    SentryUserContext.clearUser();
  },

  async login(oauthToken: string, vendorType: OAuthVendor, utm?: UtmParams): Promise<AuthTokens> {
    const response = await socialApiClient.post<{
      access_token: string;
      access_token_expire_time: number;
      email: string;
      first_name: string;
      last_name: string;
      refresh_token: string;
      refresh_token_expire_time: number;
      user_id: string;
    }>('auth/login', {
      json: omitNullish({
        oauth_token: oauthToken,
        utm: utm
          ? {
              campaign: utm?.utm_campaign,
              content: utm?.utm_content,
              medium: utm?.utm_medium,
              source: utm?.utm_source,
              term: utm?.utm_term,
            }
          : null,
        vendor_type: vendorType,
      }),
    });

    const data = await response.json();

    const authTokens: AuthTokens = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };

    AuthService.setTokens(authTokens);

    return authTokens;
  },

  async refreshToken(): Promise<void> {
    const tokens = AuthService.getTokens();
    if (!tokens) {
      throw new Error('No tokens found');
    }

    const response = await socialApiClient.post<{
      access_token: string;
      access_token_expires_at: string;
    }>('auth/refresh', {
      headers: {
        Authorization: `Bearer ${tokens.refreshToken}`,
      },
    });

    const data = await response.json();

    AuthService.setTokens({
      refreshToken: tokens.refreshToken,
      accessToken: data.access_token,
    });
  },

  isAccessTokenExpired(): boolean {
    const tokens = AuthService.getTokens();
    if (!tokens) {
      return true;
    }

    const expired = isTokenExpired(tokens.accessToken);
    return expired === true;
  },

  async logout(): Promise<void> {
    AuthService.removeTokens();
  },

  async validateInviteToken(token: string): Promise<{
    id: string;
    name: string;
    privilege: USER_ROLE;
  }> {
    const response = await socialApiClient.post<{
      id: string;
      name: string;
      privilege: USER_ROLE;
    }>('user/me/join', {
      json: {
        code: token,
      },
    });
    const workspaceAlias = await response.json();
    return workspaceAlias;
  },
};
